import React, { memo } from 'react';
import { Package } from 'lucide-react';

import {
    BOX_TYPE_CONFIG,
    INFORMATIONAL_AGING_STATUSES,
    STATUS_LABELS,
    STATUS_ORDER,
} from 'components/logistics/fulfillmentLifecycle/constants';
import StatusRow from 'components/logistics/fulfillmentLifecycle/modals/tabs/StatusRow';
import { Badge } from 'shadcn/components/ui/Badge';
import { cn } from 'shadcn/lib/utils';

/**
 * BoxTypeSection — groups status rows under a box-type header.
 *
 * Renders an icon, label, total, and remaining count for a single box type
 * (Regular / Frozen / OOB), followed by the five status rows in fixed order.
 *
 * @param {Object} props
 * @param {string} props.boxType - 'regular' | 'frozen' | 'oob'
 * @param {number} props.total - Total shipments in this box type
 * @param {number} props.remaining - Shipments not yet on truck
 * @param {Object} props.statuses - Map of status key to shipment arrays
 * @param {'order'|'shipment'} props.idMode - Which ID to display in chips
 */
function BoxTypeSection({ boxType, idMode, remaining, statuses, total }) {
    const config = BOX_TYPE_CONFIG[boxType] || {
        label: boxType,
        Icon: Package,
        iconClassName: 'text-gray-500',
    };
    const { Icon, iconClassName, label } = config;

    return (
        <div className="rounded-lg border" data-testid={`box-type-section-${boxType}`}>
            {/* Header */}
            <div className="flex items-center justify-between bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2">
                    <Icon className={cn('h-5 w-5', iconClassName)} />
                    <span className="font-semibold">{label} Boxes</span>
                    <Badge variant="secondary" className="text-xs">
                        {total} total
                    </Badge>
                </div>
                <Badge variant="outline" className="text-xs">
                    {remaining} remaining
                </Badge>
            </div>

            {/* Status rows */}
            <div>
                {STATUS_ORDER.map((statusKey) => (
                    <StatusRow
                        key={statusKey}
                        label={STATUS_LABELS[statusKey]}
                        shipments={statuses?.[statusKey] ?? []}
                        total={total}
                        idMode={idMode}
                        variant={statusKey === 'issue' ? 'issue' : undefined}
                        informational={INFORMATIONAL_AGING_STATUSES.has(statusKey)}
                    />
                ))}
            </div>
        </div>
    );
}

export default memo(BoxTypeSection);
